import { useMutation, useQuery } from '@tanstack/react-query';
import { apiClient } from '@/api/axios';
import type { M3ApiResponse } from '@/types/api';
import type {
  TransfersFilter,
  TransfersResponse,
  PaymentsFilter,
  PaymentsResponse,
  CorrectionsFilter,
  CorrectionRecord,
} from '@/types/monitoring';

// ─── Data fetch queries (POST endpoints treated as queries) ───────────────────

export function useTransfersQuery(
  filter: TransfersFilter,
  refetchInterval: number | false = false,
) {
  return useQuery({
    queryKey: ['monitoring', 'transfers', filter] as const,
    queryFn: async () => {
      const { data } = await apiClient.post<M3ApiResponse<TransfersResponse>>(
        '/api/v1/monitoring/transferMonitoring',
        filter,
      );
      return data.result;
    },
    refetchInterval,
    staleTime: 0,
  });
}

export function usePaymentsQuery(
  filter: PaymentsFilter,
  refetchInterval: number | false = false,
) {
  return useQuery({
    queryKey: ['monitoring', 'payments', filter] as const,
    queryFn: async () => {
      const { data } = await apiClient.post<M3ApiResponse<PaymentsResponse>>(
        '/api/v1/monitoring/paymentMonitoring',
        filter,
      );
      return data.result;
    },
    refetchInterval,
    staleTime: 0,
  });
}

export function useCorrectionsQuery(filter: CorrectionsFilter) {
  return useQuery({
    queryKey: ['monitoring', 'corrections', filter] as const,
    queryFn: async () => {
      const { data } = await apiClient.post<M3ApiResponse<CorrectionRecord[]>>(
        '/api/v1/monitoring/getTransactionsCorrections',
        filter,
      );
      return data.result;
    },
    staleTime: 0,
  });
}

// ─── Maintenance mutations ────────────────────────────────────────────────────

export function useCheckTransactionCorrectionMutation() {
  return useMutation({
    mutationFn: async (input: {
      transactionId: number | string;
      transactionType: string;
    }) => {
      const { data } = await apiClient.post<M3ApiResponse>(
        '/api/v1/maintenance/checkTransactionCorrection',
        { transactionId: input.transactionId, transactionType: input.transactionType },
      );
      return data;
    },
  });
}

export function useCancelTransferMutation() {
  return useMutation({
    mutationFn: async (input: { transferId: number; ticketId: string }) => {
      const { data } = await apiClient.post<M3ApiResponse>(
        '/api/v1/maintenance/cancelTransfer',
        { transferId: input.transferId, ticketId: input.ticketId },
      );
      return data;
    },
  });
}

export function useCorrectFailedTransferMutation() {
  return useMutation({
    mutationFn: async (input: {
      transferId: number;
      ticketId: string;
      externalRef: string;
    }) => {
      const { data } = await apiClient.post<M3ApiResponse>(
        '/api/v1/maintenance/correctFailedTransfer',
        {
          transferId: input.transferId,
          ticketId: input.ticketId,
          externalRef: input.externalRef,
        },
      );
      return data;
    },
  });
}

export function useCancelPaymentMutation() {
  return useMutation({
    mutationFn: async (input: { paymentId: number; ticketId: string }) => {
      const { data } = await apiClient.post<M3ApiResponse>(
        '/api/v1/maintenance/cancelPayment',
        { paymentId: input.paymentId, ticketId: input.ticketId },
      );
      return data;
    },
  });
}

export function useCorrectFailedPaymentMutation() {
  return useMutation({
    mutationFn: async (input: {
      paymentId: number;
      ticketId: string;
      externalRef: string;
    }) => {
      const { data } = await apiClient.post<M3ApiResponse>(
        '/api/v1/maintenance/correctFailedPayment',
        {
          paymentId: input.paymentId,
          ticketId: input.ticketId,
          externalRef: input.externalRef,
        },
      );
      return data;
    },
  });
}

export function useValidateCorrectFailedOperationMutation() {
  return useMutation({
    mutationFn: async (transactionCorrectionId: number) => {
      const { data } = await apiClient.post<M3ApiResponse>(
        '/api/v1/maintenance/validateCorrectFailedOperation',
        { transactionCorrectionId },
      );
      return data;
    },
  });
}

export function useRejectOperationCorrectionMutation() {
  return useMutation({
    mutationFn: async (transactionCorrectionId: number) => {
      const { data } = await apiClient.post<M3ApiResponse>(
        '/api/v1/maintenance/rejectOperationCorrection',
        { transactionCorrectionId },
      );
      return data;
    },
  });
}
